import { ForwardedRef, HTMLAttributes, ReactNode, forwardRef } from "react";
import { FormattedMessage } from "react-intl";
import classNames from "classnames";
import Typography, { TypographyVariant } from "app/pages/.shared/Typography/Typography";
import { UseFormattedAmountOptions, useFormattedAmount } from "./useFormattedAmount";
import styles from "./Amount.module.scss";

export type AmountSign = "+" | "-";

const FROM_LABEL = <FormattedMessage id="general.from.label" />;
const PER_PERSON_LABEL = <FormattedMessage id="general.amount.per.person.suffix.label.short" />;

export type AmountProps = {
	/** Raw value; rendered as its magnitude. */
	value: number;
	/** Sign shown before the amount (the only source of a sign). */
	sign?: AmountSign;
	/** Leading affix; overrides `from`. */
	prefix?: ReactNode;
	/** Trailing affix; overrides `perPerson`. */
	suffix?: ReactNode;
	/** Shorthand for a translated "Dès" prefix. */
	from?: boolean;
	/** Shorthand for a translated "/pers" suffix. */
	perPerson?: boolean;
	/** Size of the amount. */
	size?: TypographyVariant;
	/** Size of the amount from the `md` breakpoint up (desktop). */
	sizeMD?: TypographyVariant;
	/** Size of the sign; defaults to `size`. */
	signSize?: TypographyVariant;
	/** Size of the sign from the `md` breakpoint up; defaults to `sizeMD`. */
	signSizeMD?: TypographyVariant;
	/** Size of the prefix; defaults to `regular`. */
	prefixSize?: TypographyVariant;
	/** Size of the prefix from the `md` breakpoint up. */
	prefixSizeMD?: TypographyVariant;
	/** Size of the suffix; defaults to `regular`. */
	suffixSize?: TypographyVariant;
	/** Size of the suffix from the `md` breakpoint up. */
	suffixSizeMD?: TypographyVariant;
	/** Bold font on the amount; defaults to `true`. */
	isBold?: boolean;
	/** Bold font on the sign; defaults to `isBold`. */
	signIsBold?: boolean;
	/** Bold font on the prefix; defaults to `false` (a prefix is a label, not the price). */
	prefixIsBold?: boolean;
	/** Bold font on the suffix; defaults to `isBold`. */
	suffixIsBold?: boolean;
	/** Formatting overrides (currency, shop, Intl options). */
	formatOptions?: Omit<UseFormattedAmountOptions, "amount">;
} & Omit<HTMLAttributes<HTMLSpanElement>, "prefix">;

/**
 * Monetary amount with an optional sign, prefix and suffix.
 *
 * `value` is formatted (as a magnitude) from the store currency and locale; a
 * sign comes only from `sign`. Parts share a baseline and flip under RTL.
 * Renders nothing when `value` can't be formatted.
 */
export const Amount = forwardRef(
	(
		{
			value,
			sign,
			prefix,
			suffix,
			from = false,
			perPerson = false,
			size = "regular",
			sizeMD,
			signSize = size,
			signSizeMD,
			prefixSize = "regular",
			prefixSizeMD,
			suffixSize = "regular",
			suffixSizeMD,
			isBold = true,
			signIsBold = isBold,
			prefixIsBold = false,
			suffixIsBold = isBold,
			formatOptions,
			className,
			...rest
		}: AmountProps,
		ref: ForwardedRef<HTMLSpanElement>
	) => {
		const formattedAmount = useFormattedAmount({ amount: Math.abs(value), ...formatOptions });

		if (formattedAmount === null) {
			return null;
		}

		const resolvedPrefix = prefix ?? (from ? FROM_LABEL : undefined);
		const resolvedSuffix = suffix ?? (perPerson ? PER_PERSON_LABEL : undefined);

		return (
			<Typography
				component="span"
				variant={size}
				variantMD={sizeMD}
				isBold={isBold}
				{...rest}
				ref={ref}
				className={classNames(styles.amount, className)}
			>
				{resolvedPrefix && (
					<Typography
						component="span"
						variant={prefixSize}
						variantMD={prefixSizeMD}
						isBold={prefixIsBold}
					>
						{resolvedPrefix}
					</Typography>
				)}

				{sign && (
					<Typography
						component="span"
						variant={signSize}
						variantMD={signSizeMD}
						isBold={signIsBold}
						className={styles["amount__sign"]}
					>
						{sign}
					</Typography>
				)}

				{formattedAmount}

				{resolvedSuffix && (
					<Typography
						component="span"
						variant={suffixSize}
						variantMD={suffixSizeMD}
						isBold={suffixIsBold}
					>
						{resolvedSuffix}
					</Typography>
				)}
			</Typography>
		);
	}
);

Amount.displayName = "Amount";
